if (!customElements.get('es-product-gallery')) {
  customElements.define('es-product-gallery', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.es-gallery-track');
      this.slides = [...this.querySelectorAll('.es-gallery-slide')];
      this.thumbs = [...this.querySelectorAll('[data-es-index]')];
      this.strip = this.querySelector('.es-thumbnails');
      if (!this.track || !this.slides.length || this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.active = 0;
      this.animations = new Map();
      this.thumbs.forEach((button, index) => button.addEventListener('click', () => this.select(index), options));
      this.track.addEventListener('keydown', event => {
        const directions = { ArrowLeft: -1, ArrowRight: 1 };
        if (directions[event.key]) {
          event.preventDefault();
          this.select(this.active + directions[event.key]);
        }
      }, options);
      this.track.addEventListener('scroll', () => {
        if (this.animations.has(this.track) || this.track.classList.contains('is-dragging')) return;
        const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
        if (index !== this.active) this.update(index);
      }, { ...options, passive: true });
      [this.track, this.strip].filter(Boolean).forEach(element => this.enableDrag(element, options));
      this.resize = new ResizeObserver(() => {
        this.cancel(this.track);
        this.track.scrollLeft = this.active * this.track.clientWidth;
      });
      this.resize.observe(this.track);
      this.preload(0);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resize?.disconnect();
      this.animations?.forEach(id => cancelAnimationFrame(id));
    }
    preload(index) {
      [index - 1, index, index + 1].forEach(i => {
        const image = this.slides[i]?.querySelector('img');
        if (image) image.loading = 'eager';
      });
    }
    cancel(element) {
      cancelAnimationFrame(this.animations.get(element));
      this.animations.delete(element);
      element.style.scrollSnapType = '';
    }
    animate(element, destination) {
      this.cancel(element);
      destination = Math.max(0, Math.min(destination, element.scrollWidth - element.clientWidth));
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
        element.scrollLeft = destination;
        return;
      }
      const start = element.scrollLeft;
      const started = performance.now();
      element.style.scrollSnapType = 'none';
      const frame = now => {
        const progress = Math.min((now - started) / 240, 1);
        element.scrollLeft = start + (destination - start) * (1 - Math.pow(1 - progress, 3));
        if (progress < 1) this.animations.set(element, requestAnimationFrame(frame));
        else this.cancel(element);
      };
      this.animations.set(element, requestAnimationFrame(frame));
    }
    update(index) {
      this.active = Math.max(0, Math.min(index, this.slides.length - 1));
      this.preload(this.active);
      this.thumbs.forEach((button, i) => button.setAttribute('aria-pressed', String(i === this.active)));
      const selected = this.thumbs[this.active];
      if (selected && this.strip) {
        const b = selected.getBoundingClientRect();
        const s = this.strip.getBoundingClientRect();
        if (b.left < s.left || b.right > s.right) this.animate(this.strip, this.strip.scrollLeft + b.left - s.left - (s.width - b.width) / 2);
      }
    }
    select(index) {
      this.update(index);
      this.animate(this.track, this.active * this.track.clientWidth);
    }
    enableDrag(element, options) {
      let drag = null;
      let suppressClick = false;
      element.addEventListener('pointerdown', event => {
        this.cancel(element);
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        suppressClick = false;
        drag = { id: event.pointerId, x: event.clientX, start: element.scrollLeft, moved: false };
      }, options);
      element.addEventListener('pointermove', event => {
        if (!drag || event.pointerId !== drag.id) return;
        const distance = event.clientX - drag.x;
        if (!drag.moved && Math.abs(distance) < 5) return;
        if (!drag.moved) {
          drag.moved = true;
          element.classList.add('is-dragging');
          element.setPointerCapture(event.pointerId);
        }
        event.preventDefault();
        element.scrollLeft = drag.start - distance;
      }, options);
      const end = event => {
        if (!drag || event.pointerId !== drag.id) return;
        const moved = drag.moved;
        const index = Math.round(element.scrollLeft / element.clientWidth);
        suppressClick = moved;
        drag = null;
        element.classList.remove('is-dragging');
        if (element.hasPointerCapture(event.pointerId)) element.releasePointerCapture(event.pointerId);
        if (moved && element === this.track) this.select(index);
      };
      element.addEventListener('pointerup', end, options);
      element.addEventListener('pointercancel', end, options);
      element.addEventListener('lostpointercapture', end, options);
      element.addEventListener('click', event => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        suppressClick = false;
      }, { ...options, capture: true });
      element.addEventListener('dragstart', event => event.preventDefault(), options);
    }
  });
}
